import load from 'loadjs';

import { poll } from 'ggApp/utils/promise';
import loadConfiguration from 'ggApp/utils/configuration';
import { GATEWAY_FLOW_TYPE } from './constants';

import PaymentAPIService, { isPaymentMethodCreated } from './apiService';
import buildGatewayPayload from './buildGatewayPayload';

const { IXOPAY_PAYMENT_LIBRARY_URL, IXOPAY_PAYMENT_LIBRARY_VERSION } = loadConfiguration();

const pollingInterval = 750; // seconds -> ms
const pollingTries = 35;

export default class GroverPaymentSDK {
    constructor(axiosInstance) {
        this.paymentAPIService = new PaymentAPIService(axiosInstance);
    }

    gatewayLibraryInstance = null;

    getPaymentGateways(storeCode) {
        return this.paymentAPIService.getPaymentGateways(storeCode);
    }

    loadPaymentLibrary() {
        return new Promise((resolve, reject) => {
            const errorCb = (error) => reject(error);
            const successCb = () => {
                this.gatewayLibraryInstance = new window.PaymentJs(IXOPAY_PAYMENT_LIBRARY_VERSION);
                resolve(this.gatewayLibraryInstance);
            };
            const addAttribute = (_, scriptElement) => {
                // eslint-disable-next-line no-param-reassign
                scriptElement.dataset.main = 'payment-js';
            };

            load([IXOPAY_PAYMENT_LIBRARY_URL], {
                error: errorCb,
                success: successCb,
                before: addAttribute,
            });
        });
    }

    getPaymentLibrary() {
        return this.gatewayLibraryInstance;
    }

    getPaymentMethod(userId, paymentMethodId) {
        return this.paymentAPIService
            .getPaymentMethodStatus(userId, paymentMethodId)
            .then((response) => response.data.payment_method);
    }

    createPaymentMethod(paymentGateway, userId, userPayload) {
        const { id: gatewayId, flow_type: flowType } = paymentGateway;
        const payload = buildGatewayPayload({
            flowType,
            userPayload,
        });

        return this.paymentAPIService.registerPaymentMethod(gatewayId, payload).then(({ data }) => {
            const { payment_method: paymentMethod, meta } = data;
            const { checkout_url: checkoutUrl } = meta || {};
            const { id: paymentMethodId, billing_account_id: billingAccountId } = paymentMethod;

            if (flowType === GATEWAY_FLOW_TYPE.CREDIT_CARD) {
                if (checkoutUrl) {
                    // 3DS for this card is required
                    return { checkoutUrl, paymentMethodId, billingAccountId };
                }

                // 3DS for this card is NOT required
                return this.ensurePaymentMethod(userId, paymentMethodId);
            }
            if (flowType === GATEWAY_FLOW_TYPE.REDIRECT) {
                return { checkoutUrl, paymentMethodId, billingAccountId };
            }

            const flowError = new Error(
                `Payment SDK: payment flow '${flowType}' is not implemented`,
            );
            return Promise.reject(flowError);
        });
    }

    ensurePaymentMethod(userId, id) {
        return poll(
            () => this.paymentAPIService.getPaymentMethodStatus(userId, id),
            (response) => isPaymentMethodCreated(response.data.payment_method),
            pollingInterval,
            pollingTries,
        ).then(({ data }) => {
            const { payment_method: paymentMethod } = data;
            const { id: paymentMethodId, billing_account_id: billingAccountId } = paymentMethod;
            return { paymentMethodId, billingAccountId };
        });
    }
}
